import type { TechSupportDelegateGrant } from '../../shared/techsupport-delegate';
import type { TechSupportDelegateRequest, DelegateInvitePayload } from '../../shared/techsupport-delegate-invite';
import type { SupportFaqEntry } from '../../shared/techsupport-faq';
import type { UiTranslationKey } from './ui-translations';
import { formatIdentityFingerprint } from '../services/local-device-metadata';
import { renderLinkCodeQr } from './link-code-qr';
import { renderSettingsSection } from './settings-section-template';

/**
 * Master-only "Delegates" admin panel (docs/TODO.md K7, design note "Assigning an agent";
 * invite-code handshake is a K7 follow-on — see `techsupport-delegate-invite.ts`). Rendered only
 * inside a session authenticated as TECHSUPPORT_ROOT_USER_ID, alongside the pending-question
 * inbox (`support-inbox-view.ts`) — the operator-facing flow for turning another account into a
 * support agent without ever sharing the master key.
 *
 * Issuing a grant is driven by an invite code rather than a free-typed user id: the master
 * generates a code/QR, the candidate enters it in their own Settings (see
 * `support-delegate-optin-view.ts`), and the resulting signed request shows up here for explicit
 * review + one-click approval — removing the old typo/wrong-person risk entirely.
 */

export type SupportDelegatesViewDeps = {
  escapeHtml: (text: string) => string;
  text: (key: UiTranslationKey) => string;
  tf: (key: UiTranslationKey, params: Record<string, string | number>) => string;
  formatDate: (date: Date) => string;
  now?: () => number;
  /** Generates a fresh invite code (master-only); null if not currently the master session. */
  onCreateInvite: () => { code: string; expiresAt: number } | null;
  /** Targeted (remote) invite: suggestions + confirm-then-send for one specific known user. */
  knownUserIds?: () => string[];
  onPreviewInviteTarget?: (userId: string) => Promise<{ stageName: string; pub: string } | null>;
  onSendTargetedInvite?: (userId: string) => Promise<'sent' | 'unknown-user' | 'unavailable'>;
  /** Approve a pending request — reuses the same issue path as a manual re-issue/renewal. */
  onIssue: (input: { delegateUserId: string; label: string; ttlDays: number }) => void;
  onRevoke: (delegatePub: string) => void;
};

const RENEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function grantStatus(grant: TechSupportDelegateGrant, deps: SupportDelegatesViewDeps): string {
  if (grant.revokedAt) return deps.text('supportDelegatesStatusRevoked');
  if (new Date(grant.expiresAt).getTime() <= (deps.now?.() ?? Date.now())) return deps.text('supportDelegatesStatusExpired');
  return deps.text('supportDelegatesStatusActive');
}

export function renderSupportDelegatesSection(
  deps: SupportDelegatesViewDeps,
  grants: readonly TechSupportDelegateGrant[],
  delegateActivity: readonly SupportFaqEntry[],
  pendingRequests: readonly TechSupportDelegateRequest[] = [],
): void {
  const container = document.getElementById('support-delegates-section');
  if (!container) return;

  const now = deps.now?.() ?? Date.now();
  const sorted = [...grants].sort((a, b) => (a.issuedAt < b.issuedAt ? 1 : -1));
  // This section is re-rendered on every roster/poll update. Keep what the operator is typing into
  // a pending request (label, duration) and their focus — otherwise a refresh landing between
  // typing a label and pressing Approve silently issues the grant under the default label.
  const typed = new Map<string, { label: string | undefined; ttl: string | undefined }>();
  container.querySelectorAll<HTMLElement>('.support-delegate-pending-item').forEach((item) => {
    const id = item.dataset.requestId;
    if (!id) return;
    typed.set(id, {
      label: (item.querySelector('.support-delegate-pending-label') as HTMLInputElement | null)?.value,
      ttl: (item.querySelector('.support-delegate-pending-ttl') as HTMLInputElement | null)?.value,
    });
  });
  const focused = document.activeElement as HTMLElement | null;
  const focusedRequestId = focused?.closest<HTMLElement>('.support-delegate-pending-item')?.dataset.requestId;
  const focusedClass = focused?.classList.contains('support-delegate-pending-label')
    ? 'support-delegate-pending-label'
    : focused?.classList.contains('support-delegate-pending-ttl') ? 'support-delegate-pending-ttl' : '';

  container.innerHTML = renderSettingsSection(
    { title: deps.text('supportDelegatesTitle'), subtitle: deps.text('supportDelegatesHelp') },
    `
      <div style="margin-bottom:14px;">
        <button type="button" class="btn primary-btn" id="support-delegate-invite-btn" data-testid="support-delegate-invite-btn">${deps.text('supportDelegatesInvite')}</button>
        ${deps.onSendTargetedInvite ? `<button type="button" class="btn" id="support-delegate-invite-user-btn" data-testid="support-delegate-invite-user-btn">${deps.text('supportDelegatesInviteUser')}</button>` : ''}
      </div>
      <div style="font-weight:700;color:var(--text-primary);margin-bottom:6px;">${deps.text('supportDelegatesPendingTitle')}</div>
      <div style="display:grid;gap:8px;margin-bottom:16px;" id="support-delegate-pending-list">
        ${
          pendingRequests.length === 0
            ? `<div style="font-size:0.85em;color:var(--text-tertiary);">${deps.text('supportDelegatesPendingEmpty')}</div>`
            : pendingRequests
                .map(
                  (req) => `
                    <div class="support-delegate-pending-item" data-request-id="${deps.escapeHtml(req.requestId)}" data-candidate-pub="${deps.escapeHtml(req.candidatePub)}" data-candidate-userid="${deps.escapeHtml(req.candidateUserId)}" style="display:flex;flex-wrap:wrap;gap:8px;align-items:flex-end;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg-subtle);">
                      <div style="min-width:0;flex:1 1 160px;">
                        <div style="font-weight:600;">${deps.escapeHtml(req.candidateUserId)}</div>
                        <div style="font-family:monospace;font-size:0.76em;color:var(--text-secondary);">${deps.escapeHtml(formatIdentityFingerprint(req.candidatePub))}</div>
                        <div style="font-size:0.76em;color:var(--text-tertiary);">${deps.tf('supportDelegatesRequestedLabel', { date: deps.formatDate(new Date(req.requestedAt)) })}</div>
                      </div>
                      <label style="display:flex;flex-direction:column;gap:4px;font-size:0.85em;flex:1;min-width:120px;">
                        <span>${deps.text('supportDelegatesLabelLabel')}</span>
                        <input type="text" class="form-input support-delegate-pending-label" data-testid="support-delegate-pending-label" value="${deps.escapeHtml(req.candidateUserId)}" />
                      </label>
                      <label style="display:flex;flex-direction:column;gap:4px;font-size:0.85em;width:90px;">
                        <span>${deps.text('supportDelegatesTtlLabel')}</span>
                        <input type="number" class="form-input support-delegate-pending-ttl" data-testid="support-delegate-pending-ttl" min="1" max="90" value="30" />
                      </label>
                      <button type="button" class="btn primary-btn support-delegate-approve-btn" data-testid="support-delegate-approve-btn">${deps.text('supportDelegatesApprove')}</button>
                    </div>
                  `,
                )
                .join('')
        }
      </div>
      <div style="display:grid;gap:8px;margin-bottom:16px;">
        ${
          sorted.length === 0
            ? `<div style="font-size:0.85em;color:var(--text-tertiary);">${deps.text('supportDelegatesEmpty')}</div>`
            : sorted
                .map((grant) => {
                  const status = grantStatus(grant, deps);
                  const expiresAtMs = new Date(grant.expiresAt).getTime();
                  const canRevoke = !grant.revokedAt && expiresAtMs > now;
                  const canRenew = !grant.revokedAt && expiresAtMs - now < RENEW_WINDOW_MS;
                  return `
                    <div class="support-delegate-item" data-delegate-pub="${deps.escapeHtml(grant.delegatePub)}" data-delegate-userid="${deps.escapeHtml(grant.delegateUserId)}" data-delegate-label="${deps.escapeHtml(grant.label)}" style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border);border-radius:6px;background:var(--bg-subtle);">
                      <div style="min-width:0;">
                        <div style="font-weight:600;">${deps.escapeHtml(grant.label || grant.delegateUserId)}</div>
                        <div style="font-size:0.76em;color:var(--text-tertiary);">${status} · ${deps.tf('supportDelegatesExpiresLabel', { date: deps.formatDate(new Date(grant.expiresAt)) })}</div>
                      </div>
                      <div style="display:flex;gap:6px;flex:none;">
                        ${canRenew ? `<button type="button" class="btn support-delegate-renew-btn" data-testid="support-delegate-renew-btn">${deps.text('supportDelegatesRenew')}</button>` : ''}
                        ${canRevoke ? `<button type="button" class="btn support-delegate-revoke-btn" data-testid="support-delegate-revoke-btn">${deps.text('supportDelegatesRevoke')}</button>` : ''}
                      </div>
                    </div>
                  `;
                })
                .join('')
        }
      </div>
      <div style="font-weight:700;color:var(--text-primary);margin-bottom:6px;">${deps.text('supportDelegateActivityTitle')}</div>
      <div style="display:grid;gap:6px;">
        ${
          delegateActivity.length === 0
            ? `<div style="font-size:0.85em;color:var(--text-tertiary);">${deps.text('supportDelegateActivityEmpty')}</div>`
            : delegateActivity
                .map(
                  (e) => `
                    <div style="padding:8px 10px;border:1px solid var(--border);border-radius:6px;font-size:0.82em;">
                      <div>${deps.escapeHtml(e.canonicalQuestion)}</div>
                      <div style="color:var(--text-tertiary);">${deps.escapeHtml((e.answeredByDelegate || '').slice(0, 12))}… · ${deps.formatDate(new Date(e.answeredAt))}</div>
                    </div>
                  `,
                )
                .join('')
        }
      </div>
    `,
  );

  container.querySelectorAll<HTMLElement>('.support-delegate-pending-item').forEach((item) => {
    const kept = typed.get(item.dataset.requestId || '');
    if (!kept) return;
    const label = item.querySelector('.support-delegate-pending-label') as HTMLInputElement | null;
    const ttl = item.querySelector('.support-delegate-pending-ttl') as HTMLInputElement | null;
    if (label && kept.label !== undefined) label.value = kept.label;
    if (ttl && kept.ttl !== undefined) ttl.value = kept.ttl;
    if (focusedRequestId && item.dataset.requestId === focusedRequestId && focusedClass) {
      (item.querySelector(`.${focusedClass}`) as HTMLInputElement | null)?.focus();
    }
  });

  container.querySelector<HTMLButtonElement>('#support-delegate-invite-btn')?.addEventListener('click', () => {
    openInviteDialog(deps);
  });
  container.querySelector<HTMLButtonElement>('#support-delegate-invite-user-btn')?.addEventListener('click', () => {
    openTargetedInviteDialog(deps);
  });

  container.querySelectorAll<HTMLElement>('.support-delegate-pending-item').forEach((item) => {
    const candidateUserId = item.dataset.candidateUserid || '';
    item.querySelector<HTMLButtonElement>('.support-delegate-approve-btn')?.addEventListener('click', () => {
      const label = (item.querySelector('.support-delegate-pending-label') as HTMLInputElement | null)?.value.trim() || candidateUserId;
      const ttlDays = Number((item.querySelector('.support-delegate-pending-ttl') as HTMLInputElement | null)?.value) || 30;
      if (!candidateUserId) return;
      deps.onIssue({ delegateUserId: candidateUserId, label, ttlDays });
    });
  });

  container.querySelectorAll<HTMLElement>('.support-delegate-item').forEach((item) => {
    const delegatePub = item.dataset.delegatePub || '';
    const delegateUserId = item.dataset.delegateUserid || '';
    const label = item.dataset.delegateLabel || '';
    item.querySelector<HTMLButtonElement>('.support-delegate-revoke-btn')?.addEventListener('click', () => {
      if (delegatePub) deps.onRevoke(delegatePub);
    });
    item.querySelector<HTMLButtonElement>('.support-delegate-renew-btn')?.addEventListener('click', () => {
      if (delegateUserId) deps.onIssue({ delegateUserId, label, ttlDays: 30 });
    });
  });
}

/** Small invite-code dialog: generate → show code/QR + a live countdown, mirroring
 * `linked-devices-dialog.ts`'s link-code dialog (a distinct code family, never interchangeable). */
function openInviteDialog(deps: SupportDelegatesViewDeps): void {
  const generated = deps.onCreateInvite();
  if (!generated) return;
  const { code, expiresAt } = generated;
  const opener = document.activeElement as HTMLElement | null;
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'support-delegate-invite-modal';
  modal.dataset.testid = 'support-delegate-invite-modal';
  modal.innerHTML = `
    <div class="modal-content size-s" role="dialog" aria-modal="true" aria-labelledby="support-delegate-invite-title" aria-describedby="support-delegate-invite-help">
      <div class="modal-header"><h3 class="modal-title" id="support-delegate-invite-title">${deps.text('supportDelegatesInvite')}</h3></div>
      <p id="support-delegate-invite-help" style="font-size:0.85em;color:var(--text-tertiary);">${deps.text('supportDelegateInviteCodeHelp')}</p>
      <div data-testid="support-delegate-invite-code" style="font-family:monospace;font-size:0.9em;word-break:break-all;padding:10px;background:var(--bg-subtle);border:1px solid var(--border);border-radius:8px;">${deps.escapeHtml(code)}</div>
      <div role="img" aria-label="QR" style="margin:10px auto;width:180px;min-height:180px;display:flex;align-items:center;justify-content:center;border:1px solid var(--border);border-radius:8px;overflow:hidden;background:#fff;">
        <canvas id="support-delegate-invite-qr-canvas" width="180" height="180" aria-hidden="true"></canvas>
      </div>
      <div data-testid="support-delegate-invite-countdown" id="support-delegate-invite-countdown" role="timer" style="text-align:center;font-size:0.85em;color:var(--text-secondary);"></div>
      <div class="modal-actions">
        <button type="button" class="btn" id="support-delegate-invite-copy" data-testid="support-delegate-invite-copy">${deps.text('copy')}</button>
        <button type="button" class="btn primary-btn" id="support-delegate-invite-done">${deps.text('done')}</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  const qrCanvas = modal.querySelector('#support-delegate-invite-qr-canvas') as HTMLCanvasElement;
  void renderLinkCodeQr(qrCanvas, code).catch(() => {});

  const countdownEl = modal.querySelector('#support-delegate-invite-countdown') as HTMLElement;
  const now = () => deps.now?.() ?? Date.now();
  const tick = (): void => {
    const remaining = expiresAt - now();
    if (remaining <= 0) {
      countdownEl.textContent = deps.text('supportDelegateInviteExpired');
      window.clearInterval(timer);
      return;
    }
    const s = Math.ceil(remaining / 1000);
    countdownEl.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };
  const timer = window.setInterval(tick, 500);
  tick();

  modal.querySelector('#support-delegate-invite-copy')?.addEventListener('click', () => {
    navigator.clipboard?.writeText(code).catch(() => {});
  });
  const close = (): void => {
    window.clearInterval(timer);
    modal.remove();
    opener?.focus?.();
  };
  modal.querySelector('#support-delegate-invite-done')?.addEventListener('click', close);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });
}

/**
 * Redundant, no-proximity alternative to the code/QR dialog: pick a known user, confirm their
 * name + fingerprint, and send an invite addressed to their identity. They accept from their own
 * Settings wherever they are; the resulting request still lands in "Pending" for final approval.
 */
function openTargetedInviteDialog(deps: SupportDelegatesViewDeps): void {
  const opener = document.activeElement as HTMLElement | null;
  const suggestions = deps.knownUserIds?.() ?? [];
  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.id = 'support-delegate-targeted-modal';
  modal.dataset.testid = 'support-delegate-targeted-modal';
  modal.innerHTML = `
    <div class="modal-content size-s" role="dialog" aria-modal="true" aria-labelledby="support-delegate-targeted-title">
      <div class="modal-header"><h3 class="modal-title" id="support-delegate-targeted-title">${deps.text('supportDelegatesInviteUser')}</h3></div>
      <p style="font-size:0.85em;color:var(--text-tertiary);">${deps.text('supportDelegatesInviteUserHelp')}</p>
      <label style="display:flex;flex-direction:column;gap:4px;font-size:0.85em;">
        <span>${deps.text('supportDelegatesUserIdLabel')}</span>
        <input type="text" class="form-input" id="support-delegate-targeted-userid" data-testid="support-delegate-targeted-userid" list="support-delegate-targeted-suggestions" autocomplete="off" />
        <datalist id="support-delegate-targeted-suggestions">${suggestions.map((id) => `<option value="${deps.escapeHtml(id)}"></option>`).join('')}</datalist>
      </label>
      <div id="support-delegate-targeted-status" data-testid="support-delegate-targeted-status" role="status" aria-live="polite" style="font-size:0.84em;color:var(--text-secondary);min-height:1.4em;margin-top:8px;word-break:break-word;"></div>
      <div class="modal-actions">
        <button type="button" class="btn" id="support-delegate-targeted-cancel">${deps.text('cancel')}</button>
        <button type="button" class="btn" id="support-delegate-targeted-lookup" data-testid="support-delegate-targeted-lookup">${deps.text('supportDelegatesLookUp')}</button>
        <button type="button" class="btn primary-btn" id="support-delegate-targeted-send" data-testid="support-delegate-targeted-send" disabled>${deps.text('supportDelegatesSendInvite')}</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  const input = modal.querySelector('#support-delegate-targeted-userid') as HTMLInputElement;
  const status = modal.querySelector('#support-delegate-targeted-status') as HTMLElement;
  const send = modal.querySelector('#support-delegate-targeted-send') as HTMLButtonElement;
  let confirmedUserId = '';
  const close = (): void => {
    modal.remove();
    opener?.focus?.();
  };
  // Any edit invalidates the confirmation — the master must re-check name + fingerprint.
  input.addEventListener('input', () => {
    confirmedUserId = '';
    send.disabled = true;
    status.textContent = '';
  });
  modal.querySelector('#support-delegate-targeted-lookup')?.addEventListener('click', async () => {
    const userId = input.value.trim();
    if (!userId) return;
    status.textContent = '…';
    const target = await (deps.onPreviewInviteTarget?.(userId) ?? Promise.resolve(null));
    if (!modal.isConnected || input.value.trim() !== userId) return;
    if (!target) {
      status.textContent = deps.text('supportDelegatesUnknownUser');
      return;
    }
    confirmedUserId = userId;
    send.disabled = false;
    status.innerHTML = `${deps.escapeHtml(target.stageName)} · <span style="font-family:monospace;">${deps.escapeHtml(formatIdentityFingerprint(target.pub))}</span>`;
  });
  send.addEventListener('click', async () => {
    if (!confirmedUserId) return;
    send.disabled = true;
    const result = await (deps.onSendTargetedInvite?.(confirmedUserId) ?? Promise.resolve('unavailable' as const));
    if (!modal.isConnected) return;
    if (result === 'sent') {
      status.textContent = deps.text('supportDelegatesInviteSent');
      return;
    }
    status.textContent = deps.text(result === 'unknown-user' ? 'supportDelegatesUnknownUser' : 'supportDelegateInviteErrorUnavailable');
    send.disabled = false;
  });
  modal.querySelector('#support-delegate-targeted-cancel')?.addEventListener('click', close);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });
  input.focus();
}

export type { DelegateInvitePayload };
